import type { AuditEventPage, AuditEventSummary, MembershipSummary } from '@livechat/shared';
import { useEffect, useState } from 'react';

import { listAuditEvents } from '../api.js';
import { useDateFormat } from '../date-format.js';

const PAGE_SIZE = 25;

function emptyPage(): AuditEventPage {
	return { items: [], page: 1, pageSize: PAGE_SIZE, total: 0 };
}

function metadataText(event: AuditEventSummary, key: string): string | undefined {
	const value = event.metadata[key];
	return typeof value === 'string' && value ? value : undefined;
}

export function operationActionDescription(event: AuditEventSummary): string {
	if (event.action === 'conversation.whispered') {
		return event.targetId ? `Whisper sent · Chat ${event.targetId}` : 'Whisper sent';
	}
	if (event.action === 'member.profile_changed' || event.action === 'member.account_changed') {
		const label = metadataText(event, 'label') ?? 'Profile';
		const previousValue = metadataText(event, 'previousValue');
		const value = metadataText(event, 'value');
		const subject =
			label === 'Display name' ? label : `${event.targetName ?? 'Team member'} · ${label}`;
		if (previousValue && value) {
			return `${subject} changed from ${previousValue} to ${value}`;
		}
		if (value) return `${subject} changed to ${value}`;
		return `${subject} changed`;
	}
	const label = event.action
		.replaceAll('.', ' ')
		.replaceAll('_', ' ')
		.replace(/^./u, (character) => character.toUpperCase());
	const namedTarget =
		event.targetName ??
		metadataText(event, 'name') ??
		metadataText(event, 'title') ??
		metadataText(event, 'url');
	return namedTarget ? `${label} · ${namedTarget}` : label;
}

export function TenantOperations({ membership }: { membership: MembershipSummary }) {
	const { formatDateTime } = useDateFormat();
	const [page, setPage] = useState(1);
	const [result, setResult] = useState<AuditEventPage>(emptyPage);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string>();

	useEffect(() => setPage(1), [membership.tenantId]);

	useEffect(() => {
		setLoading(true);
		setError(undefined);
		void listAuditEvents(membership.tenantId, { page, pageSize: PAGE_SIZE })
			.then(setResult)
			.catch((caught: unknown) =>
				setError(
					caught instanceof Error ? caught.message : 'Could not load operations data',
				),
			)
			.finally(() => setLoading(false));
	}, [membership.tenantId, page]);

	const pageCount = Math.max(1, Math.ceil(result.total / result.pageSize));
	const first = result.total ? (result.page - 1) * result.pageSize + 1 : 0;
	const last = Math.min(result.total, result.page * result.pageSize);

	return (
		<div className="operations-page">
			<div className="subsection-heading">
				<div>
					<h3>Operations</h3>
					<p>Security, configuration and individual user-profile changes.</p>
				</div>
			</div>
			{error && <div className="alert error">{error}</div>}
			<div className="table-card paginated-table">
				<div className="paginated-table-scroll" aria-busy={loading}>
					<div className="table-head operations-row standard-table-head">
						<span>Date/time</span>
						<span>Action</span>
						<span>User</span>
					</div>
					{loading && <div className="operations-empty">Loading activity…</div>}
					{!loading &&
						result.items.map((event) => (
							<div className="table-row operations-row" key={event.id}>
								<time dateTime={event.createdAt}>
									{formatDateTime(event.createdAt)}
								</time>
								<span>{operationActionDescription(event)}</span>
								<span>{event.actorName ?? 'System'}</span>
							</div>
						))}
					{!loading && !result.items.length && (
						<div className="operations-empty">No tenant activity recorded yet.</div>
					)}
				</div>
				<footer className="data-pagination">
					<span>
						Showing {first}–{last} of {result.total}
					</span>
					<div>
						<button
							aria-label="Previous page"
							disabled={loading || page <= 1}
							onClick={() => setPage((current) => Math.max(1, current - 1))}
							type="button"
						>
							‹
						</button>
						<span>
							Page {result.page} of {pageCount}
						</span>
						<button
							aria-label="Next page"
							disabled={loading || page >= pageCount}
							onClick={() => setPage((current) => Math.min(pageCount, current + 1))}
							type="button"
						>
							›
						</button>
					</div>
				</footer>
			</div>
		</div>
	);
}
